import React, { useRef, useState, useMemo, useEffect, useCallback } from "react";
// Accept any Formik-like object to avoid strict prop mismatches with custom inputs
// (UIInput / UIReactSelect may expect extra props like `initialStatus`).
import { UIInput, UIReactSelect, UIInputDisable } from "components/Inputfields";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import Professional_Details from "assets/images/Agents/Professional_Details.png"
import Address_Details from "assets/images/Agents/Address_Details.png"
import calendar from "assets/images/Agents/calendar.png"
import DatePicker_v3 from "assets/images/Agents/DatePicker_v3.png"
import Personal_Info from "assets/images/Agents/Personal_Info.png"
import Profile_V1 from "assets/images/Agents/Profile_V1.png"
import Security_V2 from "assets/images/Agents/Security_V2.png"
import { showError } from "toast/toast";
import { components } from "react-select";
import AddressAutocomplete from "components/AddressAutocomplete";

export type SelectOption = { value: string; label: string };

// Custom dropdown indicator component
const DropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <i className="ri-arrow-down-s-line fs-5" style={{ color: '#6c757d' }}></i>
    </components.DropdownIndicator>
  );
};

// Custom styles to ensure dropdown indicator is always visible
const customSelectStyles = {
  dropdownIndicator: (provided: any, state: any) => ({
    ...provided,
    display: 'flex !important',
    opacity: 1,
    visibility: 'visible',
  }),
  indicatorSeparator: (provided: any, state: any) => ({
    ...provided,
    display: 'none',
  }),
};
const US_COUNTRY_DEFAULTS = { country: "United States", countryCode: "US" } as const;

// Select2 type for brokerage firms
type BrokerageOption = {
  value: string;
  label: string;
  id: string;
  firmDetails: {
    companyName: string;
    companyID?: string;
    companyWebsite?: string;
  };
};

export type AgentFormValues = {
  // personal
  firstName: string;
  lastName: string;
  phone1: string;
  phone2: string;
  // account
  email: string;
  password: string;
  // professional
  brokerageFirmAffiliation: string; // firm id
  officeId: string;
  agentID: string;
  licenseNumber: string;
  licenseExpiryDate: string;
  statuss: string;
  // address
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  zipcode: string;
  cityCode: string;
  stateCode: string;
  countryCode: string;
  latitude: number;
  longitude: number;
  displaySrc: string | null;
  // profile
  image: File | null;
  profilePic: string | null;
};

type Props = {
  isEdit: boolean;
  formik: any; // see note above
  brokerageFirmOptions: SelectOption[];
  officeOptions: SelectOption[];
  statusOptions: SelectOption[];
  onCancel: () => void;
  onSubmit: () => void;
  submitting?: boolean;
  displaySrc?: string;
  type?: string;
  // AsyncPaginate props for brokerage selection
  loadBrokerageOptions?: LoadOptions<BrokerageOption, any, { page: number }>;
  selectedBrokerageOption?: BrokerageOption | null;
  onBrokerageChange?: (option: BrokerageOption | null) => void;
  /** Enables Google address lock/clear behavior in Address Details. */
  enableGoogleAddressDetailsFlow?: boolean;
};

export default function AgentForm({
  isEdit,
  formik,
  brokerageFirmOptions,
  officeOptions,
  statusOptions,
  onCancel,
  onSubmit,
  submitting = false,
  displaySrc,
  type,
  // AsyncPaginate props
  loadBrokerageOptions,
  selectedBrokerageOption,
  onBrokerageChange,
  enableGoogleAddressDetailsFlow = false,
}: Props) {
  const isBrokerageAgent = type === "Brokerage_Agent";
  const useGoogleAddressFlow = Boolean(enableGoogleAddressDetailsFlow);

  const hasGoogleCoordinates =
    useGoogleAddressFlow &&
    Number(formik.values.latitude || 0) !== 0 &&
    Number(formik.values.longitude || 0) !== 0;

  const clearAddressDetailErrors = () => {
    (["city", "state", "zipcode", "country"] as const).forEach((field) => {
      formik.setFieldTouched(field, false, false);
      formik.setFieldError(field, undefined);
    });
  };

  const clearGoogleAddressFields = () => {
    formik.setFieldValue("city", "");
    formik.setFieldValue("cityCode", "");
    formik.setFieldValue("state", "");
    formik.setFieldValue("stateCode", "");
    formik.setFieldValue("zipcode", "");
    formik.setFieldValue("country", US_COUNTRY_DEFAULTS.country);
    formik.setFieldValue("countryCode", US_COUNTRY_DEFAULTS.countryCode);
    formik.setFieldValue("latitude", 0);
    formik.setFieldValue("longitude", 0);
  };

  const ensureManualCountryDefaults = () => {
    if (hasGoogleCoordinates || formik.values.country?.trim()) return;
    formik.setFieldValue("country", US_COUNTRY_DEFAULTS.country, false);
    formik.setFieldValue("countryCode", US_COUNTRY_DEFAULTS.countryCode, false);
    formik.setFieldError("country", undefined);
  };

  const handleAddressLine1Change = (value: string) => {
    formik.setFieldValue("addressLine1", value);
    if (hasGoogleCoordinates || !value.trim()) {
      clearGoogleAddressFields();
      clearAddressDetailErrors();
    } else {
      ensureManualCountryDefaults();
    }
  };

  // console.log(brokerageFirmOptions, "brokerageFirmOptions")
  // console.log(officeOptions, "officeOptions")
  // UI-only local state
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [showValidationBox, setShowValidationBox] = useState(false);
  const [pwdChecks, setPwdChecks] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
    specialChar: false,
  });
  const [preview, setPreview] = useState<string | undefined>(undefined);
  const validatePwd = (pwd: string) =>
    setPwdChecks({
      length: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      digit: /\d/.test(pwd),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
    });

  // resolve which form field holds the file (profilePic or image)
  const fieldName: "profilePic" | "image" = useMemo(() => {
    return Object.prototype.hasOwnProperty.call(formik.values ?? {}, "profilePic")
      ? "profilePic"
      : "image";
  }, [formik.values]);

  // revoke blob URL on change/unmount
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  // if parent clears the field, clear our preview too
  useEffect(() => {
    const v = formik.values?.[fieldName];
    if (!v) {
      if (preview) URL.revokeObjectURL(preview);
      setPreview(undefined);
    }
  }, [formik.values?.[fieldName]]);

  // compute src: preview first, fallback to server image
  const imgSrc: string | undefined = preview ?? (typeof displaySrc === "string" ? displaySrc : undefined);
  // ----------------------------- UI -----------------------------
  return (
    <div className="content container">
      <div className="p-4 pb-0 mb-3">
        <h3 className="m-0 ">{isEdit ? "Edit Agent" : "Add New Agent"}</h3>
        <div className="text-muted mt-1" style={{ fontSize: 14 }}>
          Create a new agent profile for your real estate team
        </div>
      </div>
      <div className="card border rounded-4 box-shadow-datatables overflow-hidden production-card">
        {/* header */}

        {/* <hr className="mb-2" style={{ borderTop: "none" }} /> */}

        {/* centered body */}
        <div className="row m-0">
          <div className="col-12 d-flex justify-content-center">
            <div className="col-md-11 py-5">
              {/* Personal + Account */}
              <div className="row g-4 mb-3">
                {/* Personal */}
                <div className="col-lg-6">
                  <div className="shadow-border-1 box-shadow-datatables p-4 bg-white">
                    <div className="mb-4">
                      <h6 className="mb-0 d-flex align-items-center order-tittle" style={{ gap: 8 }}>
                        <span
                          aria-hidden="true"
                          className="image-icons"
                          style={{
                            backgroundColor: "#f97316",
                            WebkitMaskImage: `url(${Personal_Info})`,
                            maskImage: `url(${Personal_Info})`,
                          }}
                        />
                        <span>Personal Information</span>
                      </h6>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <UIInput formik={formik} name="firstName" label="First Name" required placeholder="Enter first name" />
                      </div>
                      <div className="col-md-6">
                        <UIInput formik={formik} name="lastName" label="Last Name" required placeholder="Enter last name" />
                      </div>
                      <div className="col-md-6">
                        <UIInput formik={formik} name="phone1" label="Phone" required placeholder="Primary phone" />
                      </div>
                      <div className="col-md-6">
                        <UIInput formik={formik} name="phone2" label="Secondary Phone" placeholder="Optional" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Account */}
                <div className="col-lg-6">
                  <div className="shadow-border-1 box-shadow-datatables p-4 bg-white">
                    <div className="mb-4">
                      <h6 className="mb-0 d-flex align-items-center order-tittle" style={{ gap: 8 }}>
                        <span
                          aria-hidden="true"
                          className="image-icons"
                          style={{
                            backgroundColor: "#10b981",
                            WebkitMaskImage: `url(${Security_V2})`,
                            maskImage: `url(${Security_V2})`,
                          }}
                        />
                        <span>Account Access</span>
                      </h6>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-12">
                        {/* <UIInputDisable formik={formik} name="email" label="Email" required placeholder="Email address" disabled={isEdit} autocomplete={!isEdit ? "off" : undefined} /> */}
                        <UIInputDisable formik={formik} name="email" label="Email" required placeholder="Email address" autocomplete="off" />
                      </div>
                      {/* <div className="col-md-12">
                        <label className="form-label">
                          Password <span className="text-danger">*</span>
                        </label>
                        <div className="position-relative auth-pass-inputgroup">
                          <input
                            type={passwordVisible ? "text" : "password"}
                            className={`form-control pe-5 br-8 password-input${formik.touched.password && formik.errors.password ? " is-invalid" : ""
                              }`}
                            id="password"
                            name="password"
                            onChange={(e) => {
                              formik.handleChange(e);
                              validatePwd(e.target.value);
                            }}
                            onFocus={() => setShowValidationBox(true)}
                            onBlur={(e) => {
                              formik.handleBlur(e);
                              setShowValidationBox(false);
                            }}
                            value={formik.values.password}
                            placeholder="Enter password"
                            disabled={isEdit}
                            autoComplete={!isEdit ? "new-password" : undefined}
                          />
                          <button
                            type="button"
                            className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted password-addon material-shadow-none"
                            onClick={() => setPasswordVisible((v) => !v)}
                          >
                            <i className={passwordVisible ? "ri-eye-off-fill" : "ri-eye-fill"} />
                          </button>
                          {formik.touched.password && formik.errors.password && (
                            <div className="text-danger mt-1">{formik.errors.password as any}</div>
                          )}
                        </div>
                        {showValidationBox && (
                          <div className="bg-light border rounded p-2 mt-2 shadow-sm">
                            <small className="fw-semibold">Password must contain:</small>
                            <ul className="list-unstyled mb-0 mt-2">
                              <li><i className={pwdChecks.length ? "ri-check-line text-success" : "ri-close-line text-danger"} /><span className="ms-2">At least 8 characters</span></li>
                              <li><i className={pwdChecks.uppercase ? "ri-check-line text-success" : "ri-close-line text-danger"} /><span className="ms-2">At least one uppercase letter</span></li>
                              <li><i className={pwdChecks.lowercase ? "ri-check-line text-success" : "ri-close-line text-danger"} /><span className="ms-2">At least one lowercase letter</span></li>
                              <li><i className={pwdChecks.digit ? "ri-check-line text-success" : "ri-close-line text-danger"} /><span className="ms-2">At least one digit</span></li>
                              <li><i className={pwdChecks.specialChar ? "ri-check-line text-success" : "ri-close-line text-danger"} /><span className="ms-2">At least one special character</span></li>
                            </ul>
                          </div>
                        )}
                      </div> */}
                    </div>
                  </div>
                </div>
              </div>

              {/* Professional + Profile */}
              <div className="row g-4 mb-3">
                {/* Professional */}
                <div className="col-lg-6">
                  <div className="shadow-border-1 box-shadow-datatables p-4 bg-white">
                    <div className="mb-4">
                      <h6 className="mb-0 d-flex align-items-center order-tittle" style={{ gap: 8 }}>
                        <span
                          aria-hidden="true"
                          className="image-icons"
                          style={{
                            backgroundColor: "#8b5cf6",
                            WebkitMaskImage: `url(${Professional_Details})`,
                            maskImage: `url(${Professional_Details})`,
                          }}
                        />
                        <span>Professional Details</span>
                      </h6>
                    </div>

                    <div className="row g-3">
                      {/* Firm + Office side by side */}
                      {/* <div className="col-md-6">
                        <UIReactSelect
                          key={`firm-${brokerageFirmOptions.length}`}
                          formik={formik}
                          name="brokerageFirmAffiliation"
                          label="Brokerage Firm Affiliation"
                          options={brokerageFirmOptions ?? []}
                          placeholder="Select a firm"
                        />
                      </div> */}

                      <div className="col-md-6">
                        <label className="form-label">Brokerage Firm Affiliation</label>
                        {loadBrokerageOptions ? (
                          <AsyncPaginate
                            classNamePrefix="rs"
                            placeholder="Search brokerage firms..."
                            value={selectedBrokerageOption}
                            loadOptions={loadBrokerageOptions}
                            onChange={(selected) => {
                              if (onBrokerageChange) {
                                onBrokerageChange(selected);
                              }
                              formik.setFieldValue("brokerageFirmAffiliation", selected?.value || "");
                              formik.setFieldValue("officeId", "");
                            }}
                            onBlur={() => formik.setFieldTouched("brokerageFirmAffiliation", true)}
                            additional={{ page: 1 }}
                            defaultOptions
                            debounceTimeout={300}
                            isClearable
                            components={{ DropdownIndicator }}
                            styles={customSelectStyles}
                          />
                        ) : (
                          <UIReactSelect
                            key={`firm-${brokerageFirmOptions.length}`}
                            formik={formik}
                            name="brokerageFirmAffiliation"
                            label=""
                            options={brokerageFirmOptions ?? []}
                            placeholder="Select a firm"
                          />
                        )}
                      </div>

                      <div className="col-md-6">
                        <UIReactSelect
                          key={`office-${formik.values.brokerageFirmAffiliation}-${officeOptions.length}`}
                          formik={formik}
                          name="officeId"
                          label="Office"
                          options={officeOptions ?? []}
                          placeholder="Select an office"
                        />
                      </div>
                      {/* Agent ID / License Number */}
                      <div className="col-md-6">
                        <UIInput formik={formik} name="agentID" label="Agent ID/Code" placeholder="Enter Agent ID/Code" />
                      </div>
                      <div className="col-md-6">
                        <UIInput formik={formik} name="licenseNumber" label="License Number" placeholder="Enter license number" />
                      </div>
                      {/* License expiry (status moved to Profile) */}
                      <div className="col-md-6">
                        <label className="form-label">License Expiry Date</label>
                        <input
                          type="date"
                          className="form-control br-8"
                          id="licenseExpiryDate"
                          name="licenseExpiryDate"
                          value={formik.values.licenseExpiryDate}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          min={new Date().toISOString().split("T")[0]}
                        />
                      </div>
                      <div className="col-md-6" />
                    </div>
                  </div>
                </div>

                {/* Profile */}
                <div className="col-lg-6">
                  <div className="shadow-border-1 box-shadow-datatables p-4 bg-white">
                    <div className="mb-4">
                      <h6 className="mb-0 d-flex align-items-center order-tittle" style={{ gap: 8 }}>
                        <span
                          aria-hidden="true"
                          className="image-icons"
                          style={{
                            backgroundColor: "#14b8a6",
                            WebkitMaskImage: `url(${Profile_V1})`,
                            maskImage: `url(${Profile_V1})`,
                          }}
                        />
                        <span>Profile</span>
                      </h6>
                    </div>

                    <div className="mb-2">
                      <label className="form-label">Profile Image</label>
                    </div>

                    {/* hidden input */}
                    <input
                      id="image"
                      name="image"
                      type="file"
                      accept="image/*"
                      className="d-none"
                      onChange={(e) => {
                        const f = e.currentTarget.files?.[0];
                        // update formik
                        formik.setFieldValue("image", f ?? null);
                        // update preview (revoke previous blob if any)
                        if (preview) URL.revokeObjectURL(preview);
                        setPreview(f ? URL.createObjectURL(f) : undefined);
                        // optional: clear the input value so selecting the same file twice triggers change
                        e.currentTarget.value = "";
                      }}
                      onBlur={formik.handleBlur}
                    />

                    <div className="d-flex align-items-center gap-3 mb-3">
                      <label
                        htmlFor="image"
                        className="d-inline-flex align-items-center justify-content-center"
                        style={{
                          width: 72,
                          height: 72,
                          border: "2px dashed #d0d5dd",
                          borderRadius: 12,
                          cursor: "pointer",
                        }}
                        title="Upload image"
                      >
                        {imgSrc ? (
                          <img
                            src={imgSrc}                 // string | undefined (never null)
                            alt="Preview"
                            style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: 10 }}
                          />
                        ) : (
                          <i className="bi bi-upload" />
                        )}

                      </label>

                      <div>
                        <button
                          type="button"
                          className="btn upload-btn mt-2"
                          onClick={() => document.getElementById("image")?.click()}
                        >
                          Upload Image
                        </button>

                        <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                          JPG, PNG up to 10MB
                        </div>
                      </div>
                    </div>

                    {/* Status under upload */}
                    <div className="row">
                      <div className="col-md-8 col-lg-6">
                        <UIReactSelect
                          formik={formik}
                          name="statuss"
                          label="Status"
                          // required
                          options={statusOptions ?? []}
                          placeholder="Select status"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="shadow-border-1 box-shadow-datatables p-4 bg-white mb-1">
                <div className="mb-4">
                  <h6 className="mb-0 d-flex align-items-center order-tittle" style={{ gap: 8 }}>
                    <span
                      aria-hidden="true"
                      className="image-icons"
                      style={{
                        backgroundColor: "#ec4899",
                        WebkitMaskImage: `url(${Address_Details})`,
                        maskImage: `url(${Address_Details})`,
                      }}
                    />
                    <span>Address Details</span>
                  </h6>
                </div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">
                      Address Line 1
                       {/* <span className="text-danger">*</span> */}
                    </label>
                    <AddressAutocomplete
                      id="addressLine1"
                      name="addressLine1"
                      value={formik.values.addressLine1}
                      onChange={
                        useGoogleAddressFlow
                          ? handleAddressLine1Change
                          : (v) => {
                              formik.setFieldValue("addressLine1", v);
                              if (!v.trim()) {
                                clearGoogleAddressFields();
                                clearAddressDetailErrors();
                              }
                            }
                      }
                      onSelect={(picked) => {
                        formik.setFieldValue("addressLine1", picked.addressLine1);
                        formik.setFieldValue("city", picked.city);
                        formik.setFieldValue("cityCode", picked.cityCode);
                        formik.setFieldValue("state", picked.state);
                        formik.setFieldValue("stateCode", picked.stateCode);
                        formik.setFieldValue("zipcode", picked.zipcode);
                        formik.setFieldValue("country", picked.country);
                        formik.setFieldValue("countryCode", picked.countryCode);
                        formik.setFieldValue("latitude", picked.latitude);
                        formik.setFieldValue("longitude", picked.longitude);
                        if (useGoogleAddressFlow) {
                          formik.setFieldTouched("addressLine1", false, false);
                          formik.setFieldError("addressLine1", undefined);
                          clearAddressDetailErrors();
                        }
                      }}
                      className={`form-control br-8 ${formik.touched.addressLine1 && formik.errors.addressLine1 ? "is-invalid" : ""}`}
                      placeholder="Enter address"
                      error={formik.touched.addressLine1 && formik.errors.addressLine1 ? (formik.errors.addressLine1 as string) : undefined}
                      autoComplete={useGoogleAddressFlow ? "new-password" : undefined}
                    />
                  </div>

                  <div className="col-md-6">
                    <UIInput formik={formik} name="addressLine2" label="Address Line 2" placeholder="Apartment, suite, etc. (optional)" />
                  </div>

                  <div className="col-md-4">
                    <UIInputDisable
                      formik={formik}
                      name="city"
                      label="City"
                      placeholder="City"
                      disabled
                      autocomplete="new-password"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">State</label>
                    <input
                      type="text"
                      name="state"
                      className="form-control br-8"
                      placeholder="State"
                      value={formik.values.state}
                      disabled
                      autoComplete="new-password"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                    {formik.touched.state && formik.errors.state && (
                      <div className="invalid-feedback d-block">{formik.errors.state as string}</div>
                    )}
                  </div>
                  <div className="col-md-4">
                    <UIInputDisable
                      formik={formik}
                      name="zipcode"
                      label="Zip Code"
                      placeholder="Zip"
                      disabled
                      autocomplete="new-password"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                  </div>
                  {useGoogleAddressFlow && (
                    <div className="col-md-4">
                      <UIInputDisable
                        formik={formik}
                        name="country"
                        label="Country"
                        placeholder="United States"
                        disabled
                        autocomplete="new-password"
                        autoCorrect="off"
                        spellCheck={false}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="row m-0 mb-5">
          <div className="col-12 d-flex justify-content-center">
            <div className="col-md-11">
              <div className="d-flex align-items-center">
                <div className="ms-auto d-flex align-items-center gap-2">
                  <button
                    type="button"
                    onClick={onCancel}
                    className="btn back-btn surface-btn fw-bold fs-16"
                  >
                    <i className="bi bi-chevron-left me-2" />
                    Back
                  </button>

                  <button
                    type="submit"
                    className="btn btn-primary save-btn surface-btn fw-bold fs-16"
                    disabled={submitting}
                    onClick={onSubmit}
                  >
                    {submitting ? (isEdit ? "Saving..." : "Creating...") : isEdit ? "Save" : "Create Agent"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* <div className="px-4 py-3 d-flex justify-content-between align-items-center">
          <button type="button" className="btn btn-outline-secondary br-8" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={submitting} onClick={onSubmit}>
            {submitting ? (isEdit ? "Saving..." : "Creating...") : isEdit ? "Save" : "Create Agent"}
          </button>
        </div> */}
      </div>
    </div>
  );
}
